import { addDoc, collection } from "firebase/firestore";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { Close } from "@mui/icons-material";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";

interface UploadGalleryTabProps {
  onSuccess?: () => void;
}

function normalizeUrl(value: string) {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "";
  }

  if (
    trimmedValue.startsWith("http://") ||
    trimmedValue.startsWith("https://") ||
    trimmedValue.startsWith("/")
  ) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
}

export default function UploadGalleryTab({ onSuccess }: UploadGalleryTabProps) {
  const { showSnackbar } = useFeedback();
  const [title, setTitle] = useState("");
  const [programDate, setProgramDate] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [selectedCoverImage, setSelectedCoverImage] = useState<File | null>(null);
  const [albumUrl, setAlbumUrl] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const submitLockRef = useRef(false);

  async function handleSubmit() {
    if (submitLockRef.current) {
      return;
    }

    if (!title.trim() || !programDate.trim() || (!coverImage.trim() && !selectedCoverImage) || !albumUrl.trim()) {
      showSnackbar("Please fill title, program date, cover image, and album path");
      return;
    }

    try {
      submitLockRef.current = true;
      setIsSaving(true);
      const timestamp = new Date().toISOString();
      let finalCoverImage = coverImage.trim();

      if (selectedCoverImage) {
        const storageRef = ref(
          storage,
          `gallery/${Date.now()}-${selectedCoverImage.name}`,
        );
        await uploadBytes(storageRef, selectedCoverImage);
        finalCoverImage = await getDownloadURL(storageRef);
      }

      await addDoc(collection(firestore, "gallery"), {
        title: title.trim(),
        programDate: programDate.trim(),
        coverImage: normalizeUrl(finalCoverImage),
        albumUrl: normalizeUrl(albumUrl),
        createdAt: timestamp,
        updatedAt: timestamp,
      });
      setTitle("");
      setProgramDate("");
      setCoverImage("");
      setSelectedCoverImage(null);
      setAlbumUrl("");
      showSnackbar("Gallery item added successfully");
      onSuccess?.();
    } catch (error) {
      console.error("Error adding gallery item", error);
      showSnackbar("Failed to add gallery item");
    } finally {
      setIsSaving(false);
      submitLockRef.current = false;
    }
  }

  return (
    <Box mt={3}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Program Name"
            placeholder="Enter gallery title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Program Date"
            type="date"
            value={programDate}
            onChange={(event) => setProgramDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Cover Image URL"
            placeholder="Paste the cover image path/url"
            value={coverImage}
            onChange={(event) => {
              setCoverImage(event.target.value);
              if (event.target.value) {
                setSelectedCoverImage(null);
              }
            }}
            helperText="Use either a cover image URL or upload an image below."
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel>Upload Cover Image</FormLabel>
            <input
              type="file"
              accept="image/*"
              style={{
                border: "1px solid rgb(181, 176, 176)",
                width: "96%",
                paddingLeft: 8,
                paddingRight: 8,
                paddingTop: 16,
                paddingBottom: 16,
                borderRadius: 5,
                marginTop: 8,
              }}
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  setSelectedCoverImage(e.target.files[0]);
                  setCoverImage("");
                }
              }}
            />
            {selectedCoverImage ? (
              <Box mt={2} position="relative" display="inline-block">
                <img
                  src={URL.createObjectURL(selectedCoverImage)}
                  alt="Gallery cover preview"
                  style={{
                    maxWidth: "220px",
                    maxHeight: "180px",
                    borderRadius: 8,
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => setSelectedCoverImage(null)}
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    color: "#ffffff",
                    backgroundColor: "rgba(0,0,0,0.5)",
                  }}
                >
                  <Close />
                </IconButton>
              </Box>
            ) : null}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Google Photos Link"
            placeholder="Paste the Google Photos album link"
            value={albumUrl}
            onChange={(event) => setAlbumUrl(event.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save Gallery Item"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
